import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Megaphone, Plus, Search, Trash2, Eye, RotateCw } from 'lucide-react';
import { GlassCard, GlassButton, GlassInput, GlassSelect, GlassBadge, ModuleHeader } from '../ui';
import { getCampaigns, deleteCampaign, generateCampaign, Campaign } from '../../services/api';
import CampaignGenerationProgressModal from './CampaignGenerationProgressModal';
import './Campaigns.css';

// Generation states that surface a Retry button on the card. Resume mode
// preserves successful assets and only regenerates missing slots.
const RETRYABLE_STATES = new Set(['failed', 'cancelled', 'completed_with_gaps']);

const GOAL_LABELS: Record<string, string> = {
    lead_gen: 'Lead Gen',
    promotion: 'Promotion',
    awareness: 'Awareness',
    retention: 'Retention',
};

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'default'> = {
    active: 'success',
    draft: 'default',
    paused: 'warning',
    completed: 'info',
    archived: 'default',
};

const STATUS_OPTIONS = [
    { value: '', label: 'All statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'active', label: 'Active' },
    { value: 'paused', label: 'Paused' },
    { value: 'completed', label: 'Completed' },
    { value: 'archived', label: 'Archived' },
];

const CampaignsList: React.FC = () => {
    const navigate = useNavigate();
    const ctx = useOutletContext<{ selectedProjectId?: string }>();
    const selectedProjectId = ctx?.selectedProjectId;
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [deleting, setDeleting] = useState<string | null>(null);
    const [retryingId, setRetryingId] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getCampaigns(selectedProjectId);
            setCampaigns(data);
        } catch (err) {
            console.error('Failed to load campaigns:', err);
        } finally {
            setLoading(false);
        }
    }, [selectedProjectId]);

    useEffect(() => {
        load();
    }, [load]);

    const handleDelete = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        if (!window.confirm('Delete this campaign and all its assets?')) return;
        try {
            setDeleting(id);
            await deleteCampaign(id);
            setCampaigns(prev => prev.filter(c => c.campaign_id !== id));
        } finally {
            setDeleting(null);
        }
    };

    const filtered = campaigns.filter(c => {
        const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase());
        const matchStatus = !statusFilter || c.status === statusFilter;
        return matchSearch && matchStatus;
    });

    const formatDate = (d: string | null) => {
        if (!d) return '—';
        return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    return (
        <div className="campaigns-page">
            <ModuleHeader
                title="Campaigns"
                actions={[
                    <GlassButton key="new" variant="primary" onClick={() => navigate('/campaigns/create')}>
                        <Plus size={16} />
                        New Campaign
                    </GlassButton>
                ]}
            />

            <div className="campaigns-filter-bar">
                <GlassInput
                    placeholder="Search campaigns…"
                    value={search}
                    onChange={setSearch}
                    icon={<Search size={15} />}
                />
                <GlassSelect
                    value={statusFilter}
                    onChange={setStatusFilter}
                    options={STATUS_OPTIONS}
                />
            </div>

            {!selectedProjectId ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
                    <Megaphone size={64} style={{ opacity: 0.4, marginBottom: '16px' }} />
                    <h3 style={{ color: 'var(--text)', marginBottom: '8px' }}>Select a project</h3>
                    <p>Choose a project from the sidebar to view its campaigns</p>
                </div>
            ) : loading ? (
                <div className="campaigns-empty">
                    <div className="generating-spinner" />
                    <span>Loading campaigns…</span>
                </div>
            ) : filtered.length === 0 ? (
                <div className="campaigns-empty">
                    <Megaphone size={48} className="campaigns-empty-icon" />
                    <p style={{ margin: 0, fontWeight: 600, color: 'var(--text)' }}>
                        {campaigns.length === 0 ? 'No campaigns yet' : 'No campaigns match your filter'}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.88rem' }}>
                        {campaigns.length === 0
                            ? 'Create your first campaign to generate a full asset bundle from one brief.'
                            : 'Try a different search or status filter.'}
                    </p>
                    {campaigns.length === 0 && (
                        <GlassButton variant="primary" onClick={() => navigate('/campaigns/create')}>
                            <Plus size={16} />
                            Create First Campaign
                        </GlassButton>
                    )}
                </div>
            ) : (
                <div className="campaigns-grid">
                    {filtered.map(c => (
                        <GlassCard
                            key={c.campaign_id}
                            className="campaign-card"
                            onClick={() => navigate(`/campaigns/${c.campaign_id}`)}
                        >
                            <div className="campaign-card-header">
                                <h3 className="campaign-card-name">{c.name}</h3>
                                <GlassBadge variant={STATUS_VARIANT[c.status] || 'default'}>
                                    {c.status}
                                </GlassBadge>
                            </div>

                            <div className="campaign-card-meta">
                                <span className={`goal-badge goal-${c.campaign_goal}`}>
                                    {GOAL_LABELS[c.campaign_goal] || c.campaign_goal}
                                </span>
                                {c.generation_status !== 'not_started' && (
                                    <span className={`gen-status-badge gen-status-${c.generation_status}`}>
                                        {c.generation_status}
                                    </span>
                                )}
                            </div>

                            {c.product_service && (
                                <p style={{ margin: 0, fontSize: '0.83rem', color: 'var(--text-secondary)' }}>
                                    {c.product_service}
                                </p>
                            )}

                            <div className="campaign-card-stats">
                                <div className="campaign-stat">
                                    <span className="campaign-stat-value">{c.assets.length}</span>
                                    <span className="campaign-stat-label">Assets</span>
                                </div>
                                <div className="campaign-stat">
                                    <span className="campaign-stat-value">{c.email_sequences.length}</span>
                                    <span className="campaign-stat-label">Sequences</span>
                                </div>
                            </div>

                            <div className="campaign-card-dates">
                                {formatDate(c.start_date)} — {formatDate(c.end_date)}
                            </div>

                            <div className="campaign-card-actions" onClick={e => e.stopPropagation()}>
                                <GlassButton
                                    variant="secondary"
                                    onClick={() => navigate(`/campaigns/${c.campaign_id}`)}
                                    style={{ flex: 1 }}
                                >
                                    <Eye size={15} />
                                    View
                                </GlassButton>
                                {RETRYABLE_STATES.has(c.generation_status) && (
                                    <GlassButton
                                        variant="primary"
                                        onClick={async () => {
                                            setRetryingId(c.campaign_id);
                                            try {
                                                // Resume mode — backend
                                                // preserves the original
                                                // asset_types/variant_count
                                                // from the prior request.
                                                await generateCampaign(
                                                    c.campaign_id,
                                                    undefined, undefined, undefined, undefined,
                                                    /* resume */ true,
                                                );
                                            } catch { /* modal poller picks up status */ }
                                        }}
                                    >
                                        <RotateCw size={15} />
                                        Retry
                                    </GlassButton>
                                )}
                                <GlassButton
                                    variant="danger"
                                    onClick={e => handleDelete(e, c.campaign_id)}
                                    disabled={deleting === c.campaign_id}
                                >
                                    <Trash2 size={15} />
                                </GlassButton>
                            </div>
                        </GlassCard>
                    ))}
                </div>
            )}
            {retryingId && (
                <CampaignGenerationProgressModal
                    campaignId={retryingId}
                    isOpen={true}
                    onClose={() => setRetryingId(null)}
                    onComplete={() => {
                        setRetryingId(null);
                        load();  // refresh card states
                    }}
                    onRetry={async () => {
                        try {
                            await generateCampaign(
                                retryingId,
                                undefined, undefined, undefined, undefined,
                                /* resume */ true,
                            );
                        } catch { /* modal poller picks up status */ }
                    }}
                />
            )}
        </div>
    );
};

export default CampaignsList;
